import { useMemo } from "react";
import type { Task, ViewMode } from "../shared/types";
import { useKeyboard } from "../shared/hooks/use-keyboard";

interface AppKeyboardDeps {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  search: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
  focusTask: Task | null;
  setShowFocus: (show: boolean) => void;
  setShowShortcuts: (show: boolean | ((prev: boolean) => boolean)) => void;
  undo: () => void;
  redo: () => void;
  undoEvent: () => boolean;
  redoEvent: () => boolean;
}

export function useAppKeyboard({
  search,
  undo,
  redo,
  undoEvent,
  redoEvent,
  focusTask,
  setViewMode,
  setShowFocus,
  setShowShortcuts,
}: AppKeyboardDeps) {
  const keyboardBindings = useMemo(
    () => ({
      "ctrl+k": () => (search.isOpen ? search.close() : search.open()),
      "ctrl+z": () => {
        if (!undoEvent()) undo();
      },
      "ctrl+shift+z": () => {
        if (!redoEvent()) redo();
      },
      "1": () => setViewMode("today"),
      "2": () => setViewMode("calendar"),
      "3": () => setViewMode("tasks"),
      "4": () => setViewMode("habits"),
      "5": () => setViewMode("agenda"),
      "6": () => setViewMode("stats"),
      n: () => setViewMode("tasks"),
      f: () => {
        if (focusTask) setShowFocus(true);
      },
      "?": () => setShowShortcuts((s) => !s),
    }),
    [
      search,
      undo,
      redo,
      undoEvent,
      redoEvent,
      focusTask,
      setViewMode,
      setShowFocus,
      setShowShortcuts,
    ],
  );
  useKeyboard(keyboardBindings);
}
